import React, { useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { InputManager } from '../../../../../core/components/InputManager/index.ts'
import { AccountSourcingFields, isSourcingField } from '../../../../../core/components/AccountSourcingFields.tsx'
import { useLookups } from '../../../../global_configs/hooks/useLookups.ts'
import { updateProspectRecord } from '../../../../../state_manager/state_update_actions/fundraising/index.ts'
import { ProspectFields } from '../../../utils/fieldConfigs.ts'
import { getProspectTeamFields } from '../../../utils/fieldConfigs.ts'
import { fetchFundraisingById } from '../../../services/fundraisingService.ts'
import { SECTION_PROSPECT_TEAM } from '../../../../../state_manager/constants/strings.ts'
import { UNKNOWN_CONTACT } from '../../../../../state_manager/constants/strings.ts'
import { getAccessToken } from '../../../../../core/auth/authService'
import { store } from '../../../../../state_manager/store.ts'

export function ProspectTeam({
  prospectId,
  recordId,
  selectedContacts = { introduced_by: [], sourced_by: [], lead: [] },
  onContactsChange,
  className = '',
  formData = {},
  enableAutoPatch = false,
  includeInteractionDates = true
}: {
  prospectId?: string | null
  recordId?: string | null
  selectedContacts?: { introduced_by: any[]; sourced_by: any[]; lead: any[] }
  onContactsChange?: (contacts: { introduced_by: any[]; sourced_by: any[]; lead: any[] }) => void
  className?: string
  formData?: any
  enableAutoPatch?: boolean
  /** When false, hides Last Meeting, Last Email, First Meeting (used when full form shows them in Interaction Summary) */
  includeInteractionDates?: boolean
}) {
  const { detailsByCategory } = useLookups()
  const investorLookupId =
    detailsByCategory?.['Global Configs']?.find((option) => option.label === 'internal_company_id')?.value || null

  const dispatch = useDispatch()

  const prospectIdForPatch =
    recordId || prospectId || (formData && formData[ProspectFields.prospects__id]) || (formData && formData.id)

  const [previousContacts, setPreviousContacts] = useState(() => {
    return {
      introduced_by: [...(selectedContacts.introduced_by || [])],
      sourced_by: [...(selectedContacts.sourced_by || [])],
      lead: [...(selectedContacts.lead || [])]
    }
  })

  const hasInitializedRef = useRef(false)

  React.useEffect(() => {
    const hasSelectedData =
      selectedContacts.introduced_by?.length > 0 ||
      selectedContacts.sourced_by?.length > 0 ||
      selectedContacts.lead?.length > 0
    const hasPreviousData =
      previousContacts.introduced_by?.length > 0 ||
      previousContacts.sourced_by?.length > 0 ||
      previousContacts.lead?.length > 0

    if ((hasSelectedData && !hasPreviousData) || (!hasInitializedRef.current && hasSelectedData)) {
      setPreviousContacts({
        introduced_by: [...(selectedContacts.introduced_by || [])],
        sourced_by: [...(selectedContacts.sourced_by || [])],
        lead: [...(selectedContacts.lead || [])]
      })
      hasInitializedRef.current = true
    }
  }, [selectedContacts.introduced_by?.length, selectedContacts.sourced_by?.length, selectedContacts.lead?.length])

  const getTotalTeamMembers = () => {
    return (
      (selectedContacts.introduced_by?.length || 0) +
      (selectedContacts.sourced_by?.length || 0) +
      (selectedContacts.lead?.length || 0)
    )
  }

  const handlePatchSuccess = async (patchRecordId: string, updates: any) => {
    const currentToken = getAccessToken(store.getState().auth)
    if (prospectIdForPatch && currentToken) {
      try {
        const response = await fetchFundraisingById(prospectIdForPatch, currentToken)

        if (response.data && !response.error) {
          dispatch(updateProspectRecord({ prospectId: prospectIdForPatch, updates: response.data }))

          const fetchedProspect = response.data
          const updatedContacts = {
            introduced_by: fetchedProspect[ProspectFields.contact_accounts__introduced_by] || [],
            sourced_by: fetchedProspect[ProspectFields.contact_accounts__sourced_by] || [],
            lead: fetchedProspect[ProspectFields.contact_prospects__lead] || []
          }
          setPreviousContacts(updatedContacts)

          if (onContactsChange) {
            const transformedContacts = {
              introduced_by: (updatedContacts.introduced_by || []).map((contact: any) => ({
                value: contact.id || contact.contacts__id || '',
                label:
                  contact.prog__display_name ||
                  `${contact.first_name || ''} ${contact.last_name || ''}`.trim() ||
                  contact.email ||
                  UNKNOWN_CONTACT,
                email: contact.email || '',
                data: contact
              })),
              sourced_by: (updatedContacts.sourced_by || []).map((contact: any) => ({
                value: contact.id || contact.contacts__id || '',
                label:
                  contact.prog__display_name ||
                  `${contact.first_name || ''} ${contact.last_name || ''}`.trim() ||
                  contact.email ||
                  UNKNOWN_CONTACT,
                email: contact.email || '',
                data: contact
              })),
              lead: (updatedContacts.lead || []).map((contact: any) => ({
                value: contact.id || contact.contacts__id || '',
                label:
                  contact.prog__display_name ||
                  `${contact.first_name || ''} ${contact.last_name || ''}`.trim() ||
                  contact.email ||
                  UNKNOWN_CONTACT,
                email: contact.email || '',
                data: contact
              }))
            }
            onContactsChange(transformedContacts)
          }
        }
      } catch (error) {
        console.error('Error fetching prospect after patch:', error)
      }
    }
  }

  const handleContactChange = (key: string, value: any) => {
    const updatedContacts = { ...selectedContacts }

    if (key === ProspectFields.contact_accounts__introduced_by) {
      updatedContacts.introduced_by = Array.isArray(value) ? value : value ? [value] : []
    } else if (key === ProspectFields.contact_accounts__sourced_by) {
      updatedContacts.sourced_by = Array.isArray(value) ? value : value ? [value] : []
    } else if (key === ProspectFields.contact_prospects__lead) {
      updatedContacts.lead = Array.isArray(value) ? value : value ? [value] : []
    }

    if (onContactsChange) {
      onContactsChange(updatedContacts)
    }
  }

  // Introduced by / Sourced by belong to the prospect's investor: shown
  // read-only here, changed on the investor form.
  const prospectTeamFields = getProspectTeamFields(formData, selectedContacts, investorLookupId, includeInteractionDates)

  return (
    <FormSection
      data-debug='prospect-team-section'
      data-component='ProspectTeam'
      title={SECTION_PROSPECT_TEAM}
      titleColor='blue'
      className={className}
    >
      <div data-debug='prospect-team-content'>
        <AccountSourcingFields
          teamFields={prospectTeamFields}
          account={formData?.[ProspectFields.prospects__investor]}
          accountNoun='investor'
          inputMode='read'
          labelPosition='inline'
          className='prospect-team-sourcing-fields'
          fieldWrapping={true}
        />
        <InputManager
          inputMode='read'
          labelPosition='inline'
          fields={prospectTeamFields.filter((field) => !isSourcingField(field))}
          onChange={handleContactChange}
          enableAutoPatch={enableAutoPatch && !!prospectIdForPatch}
          patchId={prospectIdForPatch}
          recordId={prospectIdForPatch}
          onPatchSuccess={handlePatchSuccess}
          className='prospect-team-fields'
          fieldWrapping={true}
        />
      </div>
    </FormSection>
  )
}
